// src/components/common/Header.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { notificationService } from '../../services/notificationService';
import { parseUtcDate } from '../../utils/dateUtils';

const Header = ({ onToggleSidebar = () => { } }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, apiCall, hasPermission } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Refs for positioning calculations
  const notificationBtnRef = useRef(null);
  const profileBtnRef = useRef(null);
  const notificationDropdownRef = useRef(null);
  const profileDropdownRef = useRef(null);

  // Dynamic positioning function with improved calculations
  const positionDropdown = useCallback((buttonRef, dropdownRef, isProfile = false) => {
    if (!buttonRef.current || !dropdownRef.current) return;

    const button = buttonRef.current;
    const dropdown = dropdownRef.current;
    const buttonRect = button.getBoundingClientRect();
    const dropdownWidth = isProfile ? 280 : 350;

    // Get actual dropdown height after it's rendered
    dropdown.style.visibility = 'hidden';
    dropdown.style.display = 'block';
    const dropdownHeight = dropdown.offsetHeight;
    dropdown.style.visibility = 'visible';

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Use buttonRect positions directly since header is sticky
    let left = buttonRect.left;
    let top = buttonRect.bottom + 8;

    // Adjust horizontal position if dropdown would overflow
    if (left + dropdownWidth > viewportWidth - 20) {
      left = Math.max(20, viewportWidth - dropdownWidth - 20);
    }

    // For mobile, center the dropdown
    if (viewportWidth <= 768) {
      left = Math.max(10, (viewportWidth - dropdownWidth) / 2);
    }

    // Adjust vertical position if dropdown would overflow viewport
    if (buttonRect.bottom + dropdownHeight > viewportHeight - 20) {
      // Position above the button instead
      top = buttonRect.top - dropdownHeight - 8;

      // If still not enough space above, position just below the header
      if (top < 0) {
        top = buttonRect.bottom + 8;
      }
    }

    dropdown.style.left = `${left}px`;
    dropdown.style.top = `${top}px`;
  }, []);

  // Position dropdowns when they become visible
  useEffect(() => {
    if (showNotifications && notificationDropdownRef.current) {
      // Small delay to ensure dropdown is rendered
      setTimeout(() => {
        positionDropdown(notificationBtnRef, notificationDropdownRef, false);
      }, 10);
    }
  }, [showNotifications, positionDropdown]);

  useEffect(() => {
    if (showProfileMenu && profileDropdownRef.current) {
      setTimeout(() => {
        positionDropdown(profileBtnRef, profileDropdownRef, true);
      }, 10);
    }
  }, [showProfileMenu, positionDropdown]);

  // Reposition on window resize with debouncing
  useEffect(() => {
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (showNotifications && notificationDropdownRef.current) {
          positionDropdown(notificationBtnRef, notificationDropdownRef, false);
        }
        if (showProfileMenu && profileDropdownRef.current) {
          positionDropdown(profileBtnRef, profileDropdownRef, true);
        }
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [showNotifications, showProfileMenu, positionDropdown]);

  // Close dropdowns when clicking outside or pressing escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isNotificationClick = event.target.closest('.notification-container') ||
        event.target.closest('.notifications-dropdown');
      const isProfileClick = event.target.closest('.profile-container') ||
        event.target.closest('.profile-dropdown');

      if (!isNotificationClick && !isProfileClick) {
        setShowNotifications(false);
        setShowProfileMenu(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowNotifications(false);
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await notificationService.getUnreadCount();
        setUnreadCount(response.unread_count || 0);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchUnreadCount();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, []);

  // Fetch notifications when dropdown is opened
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!showNotifications) return;

      try {
        setLoading(true);
        const response = await notificationService.getNotifications({ limit: 5, unread_only: true });
        setNotifications(response.notifications || []);
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [showNotifications]);

  // Get page title based on current route
  const getPageTitle = useCallback(() => {
    const path = location.pathname;
    const titleMap = {
      '/dashboard': user?.user_type === 'core_admin' ? 'Core Admin Dashboard' : 'Dashboard',
      '/organizations': 'Organization Management',
      '/staff': user?.user_type === 'core_admin' ? 'User Management' : 'Staff Management',
      '/users': 'User Management',
      '/attendance': 'Attendance Management',
      '/reports': 'Attendance Analytics',
      '/analytics': 'Payment Analytics',
      '/admin/audit-trail': 'Audit Trail',
      '/leaves': 'Leave Management',
      '/premises': 'Premises Management',
      '/shifts': 'Shift Management',
      '/subscriptions': 'Subscriptions',
      '/payment-gateways': 'Payment Gateways',
      '/license-pricing': 'License Pricing'
    };

    return titleMap[path] || 'eHAAZRI';
  }, [location.pathname, user?.user_type]);

  // Get page subtitle/description
  const getPageSubtitle = useCallback(() => {
    const path = location.pathname;
    const subtitleMap = {
      '/dashboard': 'Welcome back! Here\'s what\'s happening today.',
      '/organizations': 'Manage all organizations and their approvals',
      '/staff': 'Manage staff members and their information',
      '/users': 'Manage all system users across organizations',
      '/attendance': 'Track and monitor attendance records',
      '/reports': 'View attendance analytics and generate reports',
      '/analytics': 'View payment and subscription analytics',
      '/admin/audit-trail': 'Track all admin data and operational activities',
      '/leaves': 'Manage leave requests and approvals',
      '/premises': 'Configure and manage office locations',
      '/shifts': 'Define work schedules and shifts',
      '/subscriptions': 'Manage subscription plans and billing',
      '/payment-gateways': 'Configure payment processing options',
      '/license-pricing': 'Manage per-country, per-staff-count pricing tiers'
    };

    return subtitleMap[path] || '';
  }, [location.pathname]);

  // Handle notification item click
  const handleNotificationItemClick = useCallback(async (notification) => {
    try {
      // Convert is_read to boolean (API returns "0" or "1" as string)
      const isRead = notification.is_read === true || notification.is_read === 1 || notification.is_read === "1";

      // Mark as read if unread
      if (!isRead) {
        await notificationService.markAsRead(notification.id);
        // Update unread count
        setUnreadCount(prev => Math.max(0, prev - 1));
        // Update notification list
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }

      // Navigate to action URL if available
      if (notification.action_url) {
        setShowNotifications(false);
        navigate(notification.action_url);
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, [navigate]);

  // Handle mark all as read
  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  }, []);

  // Format relative time
  const formatRelativeTime = useCallback((dateString) => {
    const date = parseUtcDate(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    return date.toLocaleDateString();
  }, []);

  const handleNotificationClick = useCallback((e) => {
    e.stopPropagation();
    setShowProfileMenu(false);
    setShowNotifications(!showNotifications);
  }, [showNotifications]);

  const handleProfileClick = useCallback((e) => {
    e.stopPropagation();
    setShowNotifications(false);
    setShowProfileMenu(!showProfileMenu);
  }, [showProfileMenu]);

  const handleProfileAction = useCallback(async (action) => {
    setShowProfileMenu(false);

    switch (action) {
      case 'profile':
        navigate('/profile');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'change-password':
        navigate('/change-password');
        break;
      case 'logout':
        if (window.confirm('Are you sure you want to logout?')) {
          try {
            await logout();
            navigate('/login', { replace: true });
          } catch (error) {
            console.error('Logout error:', error);
            navigate('/login', { replace: true });
          }
        }
        break;
      default:
        break;
    }
  }, [navigate, logout]);

  const formatTime = useCallback(() => {
    return new Date().toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const isOrganizationVerified = useCallback(() => {
    if (user?.user_type === 'core_admin') return true;
    const orgStatus = Number(user?.org_status);
    return !!user?.email_verified_at || orgStatus === 2;
  }, [user?.user_type, user?.email_verified_at, user?.org_status]);

  const getOrgStatusInfo = useCallback(() => {
    if (user?.user_type === 'core_admin') {
      return {
        totalOrgs: 'All Organizations',
        status: 'System Wide Access'
      };
    } else {
      return {
        totalOrgs: user?.org_name || 'Organization',
        status: isOrganizationVerified() ? 'Verified Account' : 'Pending Verification'
      };
    }
  }, [user, isOrganizationVerified]);

  const getUserIcon = useCallback(() => {
    switch (user?.user_type) {
      case 'core_admin':
        return 'fas fa-crown';
      case 'client_admin':
        return 'fas fa-user-tie';
      case 'app_admin':
        return 'fas fa-user-cog';
      default:
        return 'fas fa-user';
    }
  }, [user?.user_type]);

  const getUserRoleDisplay = useCallback(() => {
    switch (user?.user_type) {
      case 'core_admin':
        return 'Core Admin';
      case 'client_admin':
        return 'Organization Admin';
      case 'app_admin':
        return 'Premises Admin';
      default:
        return 'Staff Member';
    }
  }, [user?.user_type]);

  const orgStatus = getOrgStatusInfo();

  return (
    <>
      <header className="header">
        <div className="header-left">
          <button
            className="toggle-sidebar"
            onClick={onToggleSidebar}
            onTouchEnd={(e) => {
              e.preventDefault();
              onToggleSidebar && onToggleSidebar();
            }}
            aria-label="Toggle sidebar"
            type="button"
          >
            <i className="fas fa-bars"></i>
          </button>
          <div className="page-info">
            <h1 className="page-title">{getPageTitle()}</h1>
            <div className="page-meta">
              <span className="page-subtitle">{getPageSubtitle()}</span>
              <span className="page-time">{formatTime()}</span>
            </div>
          </div>
        </div>

        <div className="header-right">
          {/* Organization Status (for non-core admin) */}
          {user?.user_type !== 'core_admin' && (
            <div className="org-status">
              <div className="org-name">{orgStatus.totalOrgs}</div>
              <div className="org-verification">
                {isOrganizationVerified() ? (
                  <span className="verified">
                    <i className="fas fa-check-circle"></i>
                    {orgStatus.status}
                  </span>
                ) : (
                  <span className="unverified">
                    <i className="fas fa-exclamation-circle"></i>
                    {orgStatus.status}
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="header-actions">
            {/* Quick Add Button for Admins 
            {(user?.user_type === 'client_admin' || user?.user_type === 'core_admin') && (
              <div className="quick-add-container">
                <button
                  className="header-btn quick-add-btn"
                  title="Quick Add"
                  aria-label="Quick Add"
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
            )}*/}

            {/* Settings 
            {hasPermission('settings.view') && (
              <button
                className="header-btn"
                title="Settings"
                aria-label="Settings"
                onClick={() => navigate('/settings')}
              >
                <i className="fas fa-cog"></i>
              </button>
            )}*/}

            {/* Notifications */}
            <div className="notification-container">
              <button
                ref={notificationBtnRef}
                className="header-btn notification-btn"
                onClick={handleNotificationClick}
                title="Notifications"
                aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
                aria-expanded={showNotifications}
                aria-haspopup="true"
              >
                <i className="fas fa-bell"></i>
                {unreadCount > 0 && (
                  <span className="notification-badge" aria-hidden="true">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            </div>

            {/* User Profile */}
            <div className="profile-container">
              <div
                ref={profileBtnRef}
                className="user-profile"
                onClick={handleProfileClick}
                role="button"
                tabIndex={0}
                aria-label="User profile menu"
                aria-expanded={showProfileMenu}
                aria-haspopup="true"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleProfileClick(e);
                  }
                }}
              >
                <div className="user-avatar-small">
                  <i className={getUserIcon()}></i>
                </div>
                <div className="user-info-small">
                  <div className="user-name-small">{user?.username}</div>
                  <div className="user-role-small">{getUserRoleDisplay()}</div>
                </div>
                <i className="fas fa-chevron-down profile-arrow"></i>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Portal-style dropdowns positioned absolutely */}
      {showNotifications && (
        <div
          ref={notificationDropdownRef}
          className="notifications-dropdown"
          role="menu"
          aria-label="Notifications"
        >
          <div className="notifications-header">
            <h4>Notifications</h4>
            <div className="notifications-header-actions">
              <span className="notifications-count">{unreadCount} unread</span>
              {unreadCount > 0 && (
                <button
                  className="btn-mark-read"
                  onClick={handleMarkAllAsRead}
                  title="Mark all as read"
                >
                  <i className="fas fa-check-double"></i>
                </button>
              )}
            </div>
          </div>

          <div className="notifications-list">
            {loading ? (
              <div className="notifications-loading">
                <i className="fas fa-spinner fa-spin"></i>
                <p>Loading notifications...</p>
              </div>
            ) : notifications.length > 0 ? (
              notifications.map(notification => {
                const isRead = notification.is_read === true || notification.is_read === 1 || notification.is_read === "1";
                return (
                  <div
                    key={notification.id}
                    className={`notification-item ${isRead ? 'read' : 'unread'}`}
                    role="menuitem"
                    tabIndex={0}
                    onClick={() => handleNotificationItemClick(notification)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleNotificationItemClick(notification);
                      }
                    }}
                  >
                    <div className={`notification-icon ${notification.type}`}>
                      <i className={
                        notification.type === 'warning' ? 'fas fa-exclamation-triangle' :
                          notification.type === 'success' ? 'fas fa-check-circle' :
                            notification.type === 'error' ? 'fas fa-times-circle' :
                              notification.type === 'announcement' ? 'fas fa-bullhorn' :
                                'fas fa-info-circle'
                      }></i>
                    </div>
                    <div className="notification-content">
                      <div className="notification-title">{notification.title}</div>
                      <div className="notification-message">{notification.message}</div>
                      <div className="notification-time">
                        {formatRelativeTime(notification.created_at)}
                      </div>
                    </div>
                    {!isRead && <div className="notification-unread-dot"></div>}
                  </div>
                );
              })
            ) : (
              <div className="no-notifications">
                <i className="fas fa-bell-slash"></i>
                <p>No notifications</p>
              </div>
            )}
          </div>

          <div className="notifications-footer">
            <button
              className="btn-link"
              onClick={() => {
                setShowNotifications(false);
                navigate('/notifications');
              }}
            >
              View All Notifications
            </button>
          </div>
        </div>
      )}

      {showProfileMenu && (
        <div
          ref={profileDropdownRef}
          className="profile-dropdown"
          role="menu"
          aria-label="Profile menu"
        >
          <div className="profile-menu-header">
            <div className="profile-menu-avatar">
              <i className={getUserIcon()}></i>
            </div>
            <div className="profile-menu-info">
              <div className="profile-menu-name">{user?.username}</div>
              <div className="profile-menu-email">{user?.email}</div>
            </div>
          </div>

          <div className="profile-menu-divider"></div>

          <div
            className="profile-menu-item"
            onClick={() => handleProfileAction('profile')}
            role="menuitem"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleProfileAction('profile');
              }
            }}
          >
            <i className="fas fa-user"></i>
            <span>View Profile</span>
          </div>

          {/*hasPermission('settings.view') && (
            <div
              className="profile-menu-item"
              onClick={() => handleProfileAction('settings')}
              role="menuitem"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleProfileAction('settings');
                }
              }}
            >
              <i className="fas fa-cog"></i>
              <span>Settings</span>
            </div>
          )*/}

          <div
            className="profile-menu-item"
            onClick={() => handleProfileAction('change-password')}
            role="menuitem"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleProfileAction('change-password');
              }
            }}
          >
            <i className="fas fa-key"></i>
            <span>Change Password</span>
          </div>

          {user?.user_type === 'core_admin' && (
            <>
              <div className="profile-menu-divider"></div>
              <div
                className="profile-menu-item"
                onClick={() => navigate('/organizations')}
                role="menuitem"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/organizations');
                  }
                }}
              >
                <i className="fas fa-sitemap"></i>
                <span>Manage Organizations</span>
              </div>
              <div
                className="profile-menu-item"
                onClick={() => navigate('/analytics')}
                role="menuitem"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/analytics');
                  }
                }}
              >
                <i className="fas fa-money-check-dollar"></i>
                <span>Payment Analytics</span>
              </div>
            </>
          )}

          <div className="profile-menu-divider"></div>
          <div
            className="profile-menu-item logout-item"
            onClick={() => handleProfileAction('logout')}
            role="menuitem"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleProfileAction('logout');
              }
            }}
          >
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </div>
        </div>
      )}

      <style jsx>{`
      .header {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  padding: 1.25rem 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 2px solid transparent;
  border-image: linear-gradient(90deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%) 1;
  min-height: 80px;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0; /* Allow content to shrink */
}

.toggle-sidebar {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border: 1px solid #e5e7eb;
  font-size: 1.3rem;
  color: #374151;
  cursor: pointer;
  padding: 0.6rem;
  border-radius: 10px;
  transition: all 0.3s ease;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  pointer-events: auto;
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(59, 130, 246, 0.3);
}

.toggle-sidebar:hover,
.toggle-sidebar:focus-visible {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.page-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.02em;
}

.page-meta {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.page-subtitle {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 500;
  line-height: 1.3;
}

.page-time {
  font-size: 0.8rem;
  color: #6b7280;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-shrink: 0;
}

.org-status {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.org-name {
  font-weight: 600;
  color: #111827;
  font-size: 0.9rem;
}

.org-verification {
  font-size: 0.8rem;
}

.verified {
  color: #10b981;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.unverified {
  color: #f59e0b;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-btn {
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border: 1px solid #e5e7eb;
  font-size: 1.2rem;
  color: #374151;
  cursor: pointer;
  padding: 0.6rem;
  border-radius: 10px;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  min-height: 42px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.header-btn:hover,
.header-btn:focus-visible {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
  border-color: #3b82f6;
}

.quick-add-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #ffffff;
  border-radius: 50%;
  width: 42px;
  height: 42px;
  border: none;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.quick-add-btn:hover,
.quick-add-btn:focus-visible {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: scale(1.1) rotate(90deg);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5);
}

.notification-container, .profile-container {
  position: relative;
}

.notification-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: #ffffff;
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  min-width: 20px;
  text-align: center;
  line-height: 1;
  font-weight: 700;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.4);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.notifications-dropdown, .profile-dropdown {
  position: fixed;
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  border: none;
  z-index: 1001;
  min-width: 350px;
  max-height: 450px;
  overflow: hidden;
  animation: dropdownSlide 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  backdrop-filter: blur(10px);
}

.profile-dropdown {
  min-width: 280px;
  max-height: 400px;
  overflow-y: auto;
}

@keyframes dropdownSlide {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.notifications-header {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f9fafb;
}

.notifications-header h4 {
  margin: 0;
  font-weight: 600;
  color: #111827;
}

.notifications-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.notifications-count {
  font-size: 0.8rem;
  color: #6b7280;
  background: #ffffff;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
}

.btn-mark-read {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 0.9rem;
}

.btn-mark-read:hover {
  background: #dbeafe;
  color: #2563eb;
}

.notifications-list {
  max-height: 250px;
  overflow-y: auto;
}

.notifications-loading {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
}

.notifications-loading i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: #3b82f6;
}

.notification-item {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  gap: 1rem;
  transition: background 0.2s ease;
  cursor: pointer;
  position: relative;
}

.notification-item:hover,
.notification-item:focus-visible {
  background: #f9fafb;
  outline: 2px solid #3b82f6;
  outline-offset: -2px;
}

.notification-item:last-child {
  border-bottom: none;
}

.notification-item.unread {
  background: rgba(59, 130, 246, 0.05);
  border-left: 3px solid #3b82f6;
}

.notification-unread-dot {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 8px;
  height: 8px;
  background: #3b82f6;
  border-radius: 50%;
}

.no-notifications {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
}

.no-notifications i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  opacity: 0.5;
}

.notification-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  flex-shrink: 0;
}

.notification-icon.info {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.notification-icon.warning {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.notification-icon.success {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-weight: 600;
  color: #111827;
  font-size: 0.9rem;
  margin-bottom: 0.2rem;
  line-height: 1.3;
}

.notification-message {
  color: #6b7280;
  font-size: 0.8rem;
  margin-bottom: 0.3rem;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.notification-time {
  color: #6b7280;
  font-size: 0.7rem;
}

.notifications-footer {
  padding: 1rem;
  text-align: center;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.btn-link {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-link:hover,
.btn-link:focus-visible {
  text-decoration: underline;
  background: rgba(59, 130, 246, 0.1);
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.user-profile {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid #e5e7eb;
  min-width: 0;
  background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.user-profile:hover,
.user-profile:focus-visible {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-color: #3b82f6;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.2);
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.user-avatar-small {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 1rem;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  border: 2px solid #ffffff;
}

.user-info-small {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.user-name-small {
  font-weight: 600;
  color: #111827;
  font-size: 0.9rem;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-role-small {
  font-size: 0.7rem;
  color: #6b7280;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-arrow {
  font-size: 0.7rem;
  color: #6b7280;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.user-profile:hover .profile-arrow,
.user-profile:focus-visible .profile-arrow {
  transform: rotate(180deg);
}

.profile-menu-header {
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #f9fafb;
}

.profile-menu-avatar {
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.profile-menu-info {
  flex: 1;
  min-width: 0;
}

.profile-menu-name {
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.2rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-menu-email {
  font-size: 0.8rem;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.profile-menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  color: #111827;
}

.profile-menu-item:hover,
.profile-menu-item:focus-visible {
  background: #f3f4f6;
  outline: 2px solid #3b82f6;
  outline-offset: -2px;
}

.profile-menu-item.logout-item:hover,
.profile-menu-item.logout-item:focus-visible {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  outline-color: #ef4444;
}

.profile-menu-item i {
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.profile-menu-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 0.5rem 0;
}

/* Focus management for dropdowns */
.notifications-dropdown:focus-within,
.profile-dropdown:focus-within {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* Scrollbar styling for profile dropdown */
.profile-dropdown::-webkit-scrollbar {
  width: 6px;
}

.profile-dropdown::-webkit-scrollbar-track {
  background: #f3f4f6;
}

.profile-dropdown::-webkit-scrollbar-thumb {
  background: #6b7280;
  border-radius: 3px;
}

.profile-dropdown::-webkit-scrollbar-thumb:hover {
  background: #374151;
}

/* Scrollbar styling for dropdown lists */
.notifications-list::-webkit-scrollbar {
  width: 6px;
}

.notifications-list::-webkit-scrollbar-track {
  background: #f3f4f6;
}

.notifications-list::-webkit-scrollbar-thumb {
  background: #6b7280;
  border-radius: 3px;
}

.notifications-list::-webkit-scrollbar-thumb:hover {
  background: #374151;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .header {
    padding: 1rem 1.5rem;
  }

  .page-subtitle {
    font-size: 0.85rem;
  }

  .page-time {
    font-size: 0.75rem;
  }
}

@media (max-width: 1024px) {
  .header {
    padding: 1rem 1.5rem;
    min-height: 75px;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .page-subtitle {
    display: none;
  }

  .org-status {
    font-size: 0.85rem;
  }
}

@media (max-width: 768px) {
  .header {
    padding: 0.875rem 1rem;
    min-height: 65px;
    border-bottom-width: 1px;
  }

  .toggle-sidebar {
    padding: 0.5rem;
    font-size: 1.1rem;
  }

  .page-title {
    font-size: 1.25rem;
    letter-spacing: -0.01em;
  }

  .page-time {
    display: none;
  }

  .user-info-small, .org-status {
    display: none;
  }

  .header-actions {
    gap: 0.5rem;
  }

  .header-btn {
    min-width: 38px;
    min-height: 38px;
    padding: 0.5rem;
    font-size: 1.1rem;
  }

  .quick-add-btn {
    width: 38px;
    height: 38px;
  }

  .user-avatar-small {
    width: 36px;
    height: 36px;
    font-size: 0.9rem;
  }

  .user-profile {
    padding: 0.4rem 0.6rem;
  }

  .notifications-dropdown {
    min-width: 300px;
    max-width: calc(100vw - 40px);
    border-radius: 12px;
  }

  .profile-dropdown {
    min-width: 260px;
    max-width: calc(100vw - 40px);
    border-radius: 12px;
  }
}

@media (max-width: 480px) {
  .header {
    padding: 0.75rem 0.875rem;
    min-height: 60px;
  }

  .header-left {
    gap: 0.5rem;
  }

  .toggle-sidebar {
    padding: 0.45rem;
    font-size: 1rem;
  }

  .page-title {
    font-size: 1.1rem;
  }

  .header-right {
    gap: 0.5rem;
  }

  .header-actions {
    gap: 0.4rem;
  }

  .header-btn {
    min-width: 36px;
    min-height: 36px;
    padding: 0.45rem;
    font-size: 1rem;
    border-radius: 8px;
  }

  .quick-add-btn {
    width: 36px;
    height: 36px;
  }

  .user-avatar-small {
    width: 34px;
    height: 34px;
    font-size: 0.85rem;
  }

  .user-profile {
    padding: 0.35rem 0.5rem;
    border-radius: 40px;
  }

  .profile-arrow {
    display: none;
  }

  .notifications-dropdown,
  .profile-dropdown {
    left: 10px !important;
    right: 10px !important;
    width: calc(100vw - 20px) !important;
    min-width: unset;
    max-width: unset;
    border-radius: 12px;
  }

  .notifications-header,
  .notifications-footer {
    padding: 0.875rem;
  }

  .notifications-header h4 {
    font-size: 1rem;
  }

  .profile-menu-item {
    padding: 0.875rem;
    font-size: 0.95rem;
  }

  .notification-item {
    padding: 0.875rem;
  }

  .notification-icon {
    width: 28px;
    height: 28px;
  }
}

@media (max-width: 380px) {
  .header {
    padding: 0.65rem 0.75rem;
  }

  .page-title {
    font-size: 1rem;
  }

  .header-btn {
    min-width: 34px;
    min-height: 34px;
    padding: 0.4rem;
  }

  .user-avatar-small {
    width: 32px;
    height: 32px;
    font-size: 0.8rem;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .header {
    border-bottom-width: 2px;
  }

  .notification-badge {
    border-width: 3px;
  }

  .notifications-dropdown,
  .profile-dropdown {
    border-width: 2px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .notifications-dropdown,
  .profile-dropdown {
    animation: none;
  }

  .toggle-sidebar,
  .header-btn,
  .notification-item,
  .profile-menu-item,
  .user-profile {
    transition: none;
  }

  .profile-arrow {
    transition: none;
  }

  .quick-add-btn:hover {
    transform: scale(1.05);
  }

  .notification-badge {
    animation: none;
  }

  .header-btn:hover,
  .toggle-sidebar:hover,
  .user-profile:hover {
    transform: none;
  }
}

/* Print styles */
@media print {
  .header {
    position: static;
    box-shadow: none;
    border-bottom: 1px solid #000;
  }

  .header-actions,
  .notifications-dropdown,
  .profile-dropdown {
    display: none;
  }
}
      `}</style>
    </>
  );
};

export default Header;
